import { hasAccess, personName, pickI18n, shortVersion, type SubscriptionDTO } from '@akpl/shared';
import { Avatar, Button, FocusContext, IconGlobe, IconLogout, useFocusable } from '@akpl/ui-tv';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { analytics } from '../analytics';
import { api, APP_VERSION } from '../api';
import { getFirebase } from '../firebase';
import { otherLocale, setLocale } from '../i18n';
import { useRouter } from '../router';
import { useStore } from '../store';
import { useT } from '../useT';
import styles from './screens.module.css';
import type { ScreenProps } from './types';
import { useScreenFocus } from './useScreenFocus';

export const PROFILE_FOCUS = 'profile-language';
/**
 * The action row is a focusable container, like every other screen's content block, and not a
 * pair of loose buttons. Left has to walk **up** to it to leave the screen: a focused `Button`
 * is scaled (`transform: scale(1.04)`), so its box overhangs the content column by 8 px and
 * starts *inside* the rail's 48…144 px column — norigin then drops the rail from the
 * candidates, because a sibling must end before the current element starts. The container is
 * measured unscaled at x = 144, so the rail qualifies again (TV-41, docs/04 §7.1).
 */
export const PROFILE_ACTIONS_FOCUS = 'profile-actions';

function fmtTime(iso: string | undefined, locale: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(
      new Date(iso),
    );
  } catch {
    return iso;
  }
}

const ACTIVE: readonly SubscriptionDTO['status'][] = ['active', 'trialing'];

/**
 * Profile (Figma TV-06). Name / e-mail / photo, plan name, session start + end, app version
 * and the last 6 of the device id (docs/04 §3). "Cerrar sesión" → `POST /me/logout` →
 * `signOut()` → Pairing; the language switch is persisted in `localStorage`.
 *
 * `GET /me` is best-effort: with a TV token it is still a skeleton (WP0.4), so every field it
 * would provide falls back to what the session slice already knows.
 */
export default function Profile(_props: ScreenProps<'profile'>) {
  const { t, i18n } = useT();
  const locale = i18n.language === 'en' ? 'en' : 'es';
  const user = useStore((s) => s.auth.user);
  const features = useStore((s) => s.auth.features);
  const session = useStore((s) => s.session);
  const membersCount = useStore((s) => Object.keys(s.members).length);
  const device = useStore((s) => s.device);
  const openDialog = useStore((s) => s.openDialog);
  const closeDialog = useStore((s) => s.closeDialog);
  const clearSession = useStore((s) => s.clearSession);
  const setStoreLocale = useStore((s) => s.setLocale);
  const showToast = useStore((s) => s.showToast);
  const reset = useRouter((s) => s.reset);
  const [loggingOut, setLoggingOut] = useState(false);
  useScreenFocus('profile', PROFILE_FOCUS);

  const { ref: actionsRef, focusKey: actionsKey } = useFocusable<HTMLDivElement>({
    focusKey: PROFILE_ACTIONS_FOCUS,
    trackChildren: true,
    saveLastFocusedChild: true,
  });

  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me(), retry: false });

  const photoUrl = me.data?.user.photoUrl ?? user?.photoUrl ?? null;
  const email = me.data?.user.email ?? null;
  // UX-301: never the raw address local part, whatever the stored display name says.
  const name =
    personName(me.data?.user.displayName ?? user?.displayName, email) || t('common:app.name');
  /**
   * The address is shown in full (owner request 2026-10-05, reverting UX-314's `c•••@domain`
   * mask): on the TV the room needs to see exactly which account is signed in. Display only —
   * it is never logged or sent to analytics.
   */
  const emailLine = email ? email : user?.isAnonymous ? t('tv:profile.noEmail') : (user?.id ?? '');

  // Phase 1 is a single product (docs/07 §1): the name comes from the subscription, and a caller
  // who has access but no row yet (a grant redeemed on this login) simply reads "active".
  const plan = me.data?.subscriptions.find(
    (sub) => sub.productKind === 'plan' && ACTIVE.includes(sub.status),
  );
  const planName = plan
    ? pickI18n(plan.productName, locale)
    : hasAccess(features)
      ? t('tv:profile.planActive')
      : null;

  const featureLine = features
    .map((f) =>
      f === 'tone.change'
        ? t('common:features.toneChange')
        : f === 'queue.unlimited'
          ? t('common:features.queueUnlimited')
          : null,
    )
    .filter(Boolean)
    .join(' · ');

  /** `POST /me/logout` closes the session server-side; the local sign-out happens either way. */
  const logout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await api.meLogout();
    } catch {
      showToast(t('tv:profile.logoutError'), 'error');
    }
    // `source: 'tv'` separates this from the outbox row `POST /me/logout` writes server-side.
    analytics.track('logout', { source: 'tv' });
    try {
      await getFirebase()?.signOut();
    } catch {
      /* the local session is cleared regardless */
    }
    analytics.reset();
    clearSession();
    reset('pairing');
    showToast(t('tv:profile.loggedOut'));
  };

  const onLogout = () =>
    openDialog({
      id: 'logout',
      title: t('tv:profile.logoutTitle'),
      body: t('tv:profile.logoutBody'),
      actions: [
        { key: 'cancel', label: t('common:actions.cancel'), onSelect: closeDialog },
        {
          key: 'logout',
          label: t('tv:profile.logout'),
          primary: true,
          onSelect: () => {
            closeDialog();
            void logout();
          },
        },
      ],
    });

  const onLanguage = async () => {
    const next = otherLocale(locale);
    await setLocale(next);
    setStoreLocale(next);
    showToast(t('common:language.label') + ': ' + t(`common:language.${next}`));
  };

  return (
    <div className={styles.content} data-testid="screen-profile">
      <div className={styles.profileHeader}>
        <Avatar name={name} src={photoUrl} size={160} />
        <div style={{ marginLeft: 40, minWidth: 0 }}>
          <h1 className={styles.profileName}>{name}</h1>
          <div className={styles.profileEmail} data-testid="profile-email">
            {emailLine}
          </div>
        </div>
      </div>

      <div className={styles.cards}>
        {/* The plan is named once, here, next to what it includes. The header used to repeat it
            as a badge *and* as "Plan <name>" — three copies of the same words on one screen. */}
        <div className={styles.infoCard} data-testid="profile-plan">
          <div className={styles.infoKicker}>{t('tv:profile.sectionPlan')}</div>
          <div className={styles.infoTitle}>{planName ?? t('tv:profile.noPlan')}</div>
          <div className={styles.infoBody}>{featureLine || t('tv:profile.manageHint')}</div>
        </div>
        <div className={styles.infoCard}>
          <div className={styles.infoKicker}>{t('tv:profile.sectionSession')}</div>
          <div className={styles.infoTitle}>
            {t('tv:profile.started', {
              start: fmtTime(session?.startedAt, locale),
              end: fmtTime(session?.expiresAt, locale),
            })}
          </div>
          <div className={styles.infoBody}>
            {session ? t('tv:profile.joinCode', { code: session.joinCode }) : '—'}
            {' · '}
            {t('tv:profile.connected', { count: membersCount })}
          </div>
        </div>
        <div className={styles.infoCard}>
          <div className={styles.infoKicker}>{t('tv:profile.sectionApp')}</div>
          <div className={styles.infoTitle}>
            {t('tv:profile.version', { version: shortVersion(APP_VERSION) })}
          </div>
          <div className={styles.infoBody}>
            {t('tv:profile.deviceLine', {
              id: device.id.slice(-6).toUpperCase(),
              model: device.info?.model || device.bridge,
            })}
          </div>
        </div>
      </div>

      <FocusContext.Provider value={actionsKey}>
        <div ref={actionsRef} className={styles.buttonRow} data-testid="profile-actions">
          <Button
            focusKey={PROFILE_FOCUS}
            variant="primary"
            icon={<IconGlobe size={36} />}
            onSelect={() => void onLanguage()}
          >
            {t('tv:profile.languageButton', { name: t(`common:language.${locale}`) })}
          </Button>
          <Button
            focusKey="profile-logout"
            icon={<IconLogout size={36} />}
            disabled={loggingOut}
            data-testid="profile-logout"
            onSelect={onLogout}
          >
            {loggingOut ? t('tv:profile.loggingOut') : t('tv:profile.logout')}
          </Button>
        </div>
      </FocusContext.Provider>
      <p className={styles.hint}>{t('tv:profile.manageHint')}</p>
    </div>
  );
}
